<template>
  <v-container grid-list-lg fluid>
    <v-layout wrap>
      <v-flex xs12 lg6 order-lg2 class='pa-3'>
        <v-card>
          <v-card-title class='headline font-weight-regular oem-color'>
            Load Overview
            <v-spacer/>
            <v-btn
              flat
              class='transparent ma-0'
              @click='$refs.legend.show()'
              icon
              small
            >
              <v-icon class='white--text oem-color'>
                help_outline
              </v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
          <helpLegend ref='legend' page='load'></helpLegend>
          <div class='img-overlay'>
            <device-image type='ct'></device-image>
            <div
                v-for='(selector, index) in loadSelectors'
                :key='index'
                :style='buttonLocation(index)'
                class='button-holder'
              >
              <selector
                :index='index'
                :disabled='true'
                :text='selector.text'
                :state='selector.state'
              ></selector>
            </div>
          </div>
          </v-card-text>
        </v-card>
      </v-flex>
      <v-flex xs12 lg6 class='pa-3'>
        <v-card>
          <v-card-title class='headline font-weight-regular oem-color'>
            Load Configuration
            <v-spacer/>
            <v-btn
              class='oem-color ma-0'
              @click='addLoad()'
              flat
              small
            >
              <v-icon>playlist_add</v-icon>
              Add Load
            </v-btn>
          </v-card-title>
          <v-card-text>
            <div>
              <v-data-table
                :headers='headers'
                :items='storeState.data.loads'
                must-sort
                hide-actions=''
              >
                <template v-slot:items='props'>
                  <td>{{ props.item.id + 1 }}</td>
                  <td class='text-truncate' :style='{"max-width": "100px"}'>
                    {{ props.item.name }}
                  </td>
                  <td v-if='bacnet()'>{{ props.item.vdi }}</td>
                  <td>{{ props.item.input + 1 }}</td>
                  <td v-for='(ct, index) in props.item.cts' :key='index'>
                    {{ ct.channel == 0 ? 'None' : 'CT' + (ct.channel - 5) }}
                  </td>
                  <td>
                    <v-icon small class='mr-2' @click='editItem(props.item)'>
                      edit
                    </v-icon>
                    <v-icon small @click='deleteItem(props.item)'>
                      delete
                    </v-icon>
                  </td>
                </template>
              </v-data-table>
              <v-dialog v-model='dialog' persistent max-width='550px'>
                <v-card>
                  <v-card-title class='headline font-weight-regular oem-color'
                    >{{ formTitle }}
                  </v-card-title>

                  <v-card-text>
                    <v-form ref='form' v-model='valid' lazy-validation>
                      <v-container grid-list-md>
                        <v-layout wrap>
                          <v-flex xs12 sm6 md4>
                            <v-text-field
                              v-model='editedItem.name'
                              label='Name'
                              counter
                              maxlength='40'
                              :rules='notEmptyRules'
                            ></v-text-field>
                          </v-flex>
                          <v-flex xs12 sm6 md4>
                            <v-select
                              v-model.number='editedItem.input'
                              :items='inputs()'
                              :rules='selectRules'
                              label='Voltage input'
                              @change='clearInvisibleCts(editedItem.input)'
                            ></v-select>
                          </v-flex>
                          <template v-if='bacnet()'>
                            <v-flex xs12 sm6 md4>
                              <v-text-field
                                v-model.number='editedItem.vdi'
                                mask='########'
                                label='VDI'
                                v-validate="{vdi:storeState,min_value:0,max_value:4194303}"
                                hint='0 - 4194303'
                                data-vv-name="VDI"
                                @change='validate()'
                                :error-messages="errors.collect('VDI')"
                              ></v-text-field>
                            </v-flex>
                          </template>
                          <v-flex xs12 sm6 md4 v-if='servicePhases(editedItem.input) >= 1'>
                            <v-select
                              v-model.number="editedItem.cts[0].channel"
                              :items='unusedCtsForL1'
                              :rules='selectRules'
                              label='L1'
                              @change='ctValidation(), updateUnusedCts()'
                              :error-messages='errorMsg'
                            ></v-select>
                          </v-flex>
                          <v-flex xs12 sm6 md4 v-if='servicePhases(editedItem.input) >= 2 || (editedItem.input != -1 && this.storeState.data.inputs[editedItem.input].service == 4)'>
                            <v-select
                              v-model.number="editedItem.cts[1].channel"
                              :items='unusedCtsForL2'
                              :rules='selectRules'
                              label='L2'
                              @change='ctValidation(), updateUnusedCts()'
                              :error-messages='errorMsg'
                            ></v-select>
                          </v-flex>
                          <v-flex xs12 sm6 md4 v-if='servicePhases(editedItem.input) >= 3'>
                            <v-select
                              v-model.number="editedItem.cts[2].channel"
                              :items='unusedCtsForL3'
                              :rules='selectRules'
                              label='L3'
                              @change='ctValidation(), updateUnusedCts()'
                              :error-messages='errorMsg'
                            ></v-select>
                          </v-flex>
                        </v-layout>
                      </v-container>
                    </v-form>
                  </v-card-text>

                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn class='oem-button' flat @click='close'>Cancel</v-btn>
                    <v-btn
                      class='oem-button'
                      flat
                      @click='save'
                      :disabled='!valid'
                    >
                      Save
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
              <msgDialog
                ref='deleteDialog'
                v-bind:title='`Confirm`'
                v-bind:msg='deleteText'
              />
            </div>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { Validator } from 'vee-validate';
import msgDialog from './BaseDialog.vue';
import selector from './BaseSelector.vue';
import helpLegend from './BaseLegend.vue';
import deviceImage from './BaseDeviceImage.vue';

Validator.extend('vdi', {
  getMessage: 'This VDI address is already in use.',
  validate(value, args) {
    return value === '' || args.vdis.indexOf(value) === -1;
  },
});

export default {
  name: 'Load',

  computed: {

    formTitle() {
      return this.editedIndex === -1 ? 'New Load' : 'Edit Load';
    },
    loadSelectors() {
      const selectors = [];
      for (let i = 0; i < this.storeState.data.allCts.length; i += 1) {
        selectors.push({
          text: '-',
          state: 3,
        });
      }
      Object.values(this.storeState.data.allCts).forEach((ct) => {
        if (ct.configured) {
          selectors[ct.channel - 6].state = 2;
        }
      });
      Object.values(this.storeState.data.loads).forEach((load) => {
        if (load.active) {
          let state = 0;
          if (!this.storeState.data.inputs[load.input].active) {
            state = 1;
          }
          for (let i = 0; i < 3; i += 1) {
            if (load.cts[i].active) {
              selectors[load.cts[i].channel - 6] = {
                text: `${load.id + 1}`,
                state: Math.max(state, this.ctState(load.cts[i])),
              };
            }
          }
        }
      });
      return selectors;
    },
    headers() {
      const list = [
        { text: '#', value: 'id' },
        { text: 'Name', value: 'name' },
      ];
      if (this.bacnet()) {
        list.push({ text: 'VDI', value: 'vdi' });
      }
      list.push({ text: 'Vin', value: 'input' });
      list.push({ text: 'L1', value: 'cts[0]', sortable: false });
      list.push({ text: 'L2', value: 'cts[1]', sortable: false });
      list.push({ text: 'L3', value: 'cts[2]', sortable: false });
      list.push({ text: 'Actions', value: 'name', sortable: false });

      return list;
    },
    defaultVdi() {
      return null;
      // const instance = this.storeState.data.communications.bacnet.baseInstance;
      // const loads = this.storeState.data.loads.length;
      // return instance + loads + 1;
    },
  },

  components: {
    msgDialog,
    selector,
    helpLegend,
    deviceImage,
  },

  data() {
    return {
      editedIndex: -1,
      dialog: false,
      pagination: {
        sortBy: 'name',
      },
      storeState: this.$store.state,
      errorMsg: [],
      editedItem: {
        cts: [{ channel: 0 }, { channel: 0 }, { channel: 0 }],
        active: true,
        input: -1,
        vdi: -1,
      },
      defaultItem: {
        cts: [{ channel: 0 }, { channel: 0 }, { chupdateUnusedCtsannel: 0 }],
        active: true,
        input: -1,
        vdi: -1,
      },
      valid: true,
      selectRules: [v => !!v || v === 0 || 'Field is required'],
      notEmptyRules: [v => !!v || 'Field is required'],
      deleteText: '',
      buttonLocation12: [
        { top: '13.0%', left: '91.8%' },
        { top: '23.1%', left: '91.8%' },
        { top: '33.3%', left: '91.8%' },
        { top: '43.4%', left: '91.8%' },
        { top: '53.5%', left: '91.8%' },
        { top: '63.6%', left: '91.8%' },
        { top: '13.0%', left: '8.5%' },
        { top: '23.1%', left: '8.5%' },
        { top: '33.3%', left: '8.5%' },
        { top: '43.4%', left: '8.5%' },
        { top: '53.5%', left: '8.5%' },
        { top: '63.6%', left: '8.5%' },
      ],
      buttonLocation48: [
        { top: '5.15%', left: '78%' },
        { top: '9.20%', left: '78%' },
        { top: '13.25%', left: '78%' },
        { top: '17.30%', left: '78%' },
        { top: '21.35%', left: '78%' },
        { top: '25.35%', left: '78%' },

        { top: '5.15%', left: '22%' },
        { top: '9.20%', left: '22%' },
        { top: '13.25%', left: '22%' },
        { top: '17.30%', left: '22%' },
        { top: '21.35%', left: '22%' },
        { top: '25.35%', left: '22%' },

        { top: '38.50%', left: '78%' },
        { top: '42.55%', left: '78%' },
        { top: '46.60%', left: '78%' },
        { top: '52.65%', left: '78%' },
        { top: '56.70%', left: '78%' },
        { top: '60.75%', left: '78%' },
        { top: '66.80%', left: '78%' },
        { top: '70.85%', left: '78%' },
        { top: '74.90%', left: '78%' },

        { top: '38.50%', left: '53%' },
        { top: '42.55%', left: '53%' },
        { top: '46.60%', left: '53%' },
        { top: '52.65%', left: '53%' },
        { top: '56.70%', left: '53%' },
        { top: '60.75%', left: '53%' },
        { top: '66.80%', left: '53%' },
        { top: '70.85%', left: '53%' },
        { top: '74.90%', left: '53%' },

        { top: '38.50%', left: '47%' },
        { top: '42.55%', left: '47%' },
        { top: '46.60%', left: '47%' },
        { top: '52.65%', left: '47%' },
        { top: '56.70%', left: '47%' },
        { top: '60.75%', left: '47%' },
        { top: '66.80%', left: '47%' },
        { top: '70.85%', left: '47%' },
        { top: '74.90%', left: '47%' },

        { top: '38.50%', left: '22%' },
        { top: '42.55%', left: '22%' },
        { top: '46.60%', left: '22%' },
        { top: '52.65%', left: '22%' },
        { top: '56.70%', left: '22%' },
        { top: '60.75%', left: '22%' },
        { top: '66.80%', left: '22%' },
        { top: '70.85%', left: '22%' },
        { top: '74.90%', left: '22%' },
      ],
      unusedCtsForL1: [],
      unusedCtsForL2: [],
      unusedCtsForL3: [],
    };
  },

  methods: {
    updateUnusedCts() {
      this.unusedCtsForL1 = this.storeState.unusedCts.filter(ct => ct.value === 0
                            || ((this.editedItem.cts[1].channel === 0 || ct.value !== this.editedItem.cts[1].channel)
                             && (this.editedItem.cts[2].channel === 0 || ct.value !== this.editedItem.cts[2].channel)));
      this.unusedCtsForL2 = this.storeState.unusedCts.filter(ct => ct.value === 0
                            || ((this.editedItem.cts[0].channel === 0 || ct.value !== this.editedItem.cts[0].channel)
                             && (this.editedItem.cts[2].channel === 0 || ct.value !== this.editedItem.cts[2].channel)));
      this.unusedCtsForL3 = this.storeState.unusedCts.filter(ct => ct.value === 0
                            || ((this.editedItem.cts[0].channel === 0 || ct.value !== this.editedItem.cts[0].channel)
                             && (this.editedItem.cts[1].channel === 0 || ct.value !== this.editedItem.cts[1].channel)));
    },
    buttonLocation(index) {
      if (this.storeState.data.deviceInfo.model === 48) {
        return this.buttonLocation48[index];
      }
      return this.buttonLocation12[index];
    },

    bacnet() {
      if (this.storeState.data.communications.type === 'bac_ip'
        || this.storeState.data.communications.type === 'bac_mstp') {
        return true;
      }
      return false;
    },

    changeSort(column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending;
      } else {
        this.pagination.sortBy = column;
        this.pagination.descending = false;
      }
    },

    editItem(item) {
      this.$store.updateCts(item);
      this.$store.updateVdis(item);
      this.editedIndex = this.storeState.data.loads.indexOf(item);
      this.editedItem = JSON.parse(JSON.stringify(item));
      this.dialog = true;
      this.updateUnusedCts();
      this.validate();
    },

    deleteItem(item) {
      this.deleteText = `Are you sure you want to delete ${item.name}`;
      this.$refs.deleteDialog
        .pop()
        .then(() => {
          const delList = [];
          for (let ai = 0; ai < this.storeState.data.alarms.length; ai += 1) {
            // Delete load related alarms
            if (this.storeState.alarmTypes.find(t => t.value === this.storeState.data.alarms[ai].type).loadApplicable) {
              if (this.storeState.data.alarms[ai].vinLineLoadOrCt === item.id) {
                delList.push(ai);
              }
            }
            // delete load->ct related alarms
            if (this.storeState.alarmTypes.find(t => t.value === this.storeState.data.alarms[ai].type).ctApplicable) {
              for (let ctIndex = 0; ctIndex < 3; ctIndex += 1) {
                if (item.cts[ctIndex].active) {
                  if (this.storeState.data.alarms[ai].vinLineLoadOrCt === item.cts[ctIndex].channel - 6) {
                    delList.push(ai);
                    break;
                  }
                }
              }
            }
          }

          // Sort and Traverse through the list of alarms which needs to be deleted
          delList.sort((a, b) => b - a);
          for (let idx = 0; idx < delList.length; idx += 1) {
            this.deleteAlarm(this.storeState.data.alarms[delList[idx]]);
          }

          // Reset load index for alarms
          const index = this.storeState.data.loads.indexOf(item);
          if (this.storeState.data.loads.length > index + 1) {
            for (let aIndex = 0; aIndex < this.storeState.data.alarms.length; aIndex += 1) {
              if (this.storeState.alarmTypes.find(t => t.value === this.storeState.data.alarms[aIndex].type).loadApplicable) {
                if (this.storeState.data.alarms[aIndex].vinLineLoadOrCt > item.id) {
                  this.storeState.data.alarms[aIndex].vinLineLoadOrCt -= 1;
                }
              }
            }
          }
          this.storeState.data.loads.splice(index, 1);
          for (let i = index; i < this.storeState.data.loads.length; i += 1) {
            this.storeState.data.loads[i].id -= 1;
          }

          this.$store.updateCts();
          this.$store.updateVdis();
          this.$store.updateInputs();
          this.storeState.loadDirty = true;
        })
        .catch(() => {});
    },
    deleteAlarm(item) {
      const index = this.storeState.data.alarms.indexOf(item);
      if (index !== undefined) {
        this.storeState.data.alarms.splice(index, 1);
        for (let i = index; i < this.storeState.data.alarms.length; i += 1) {
          this.storeState.data.alarms[i].id -= 1;
        }
      }
      this.storeState.alarmsDirty = true;
    },
    close() {
      this.dialog = false;
      setTimeout(() => {
        this.editedItem = JSON.parse(JSON.stringify(this.defaultItem));
        this.editedIndex = -1;
        this.$refs.form.resetValidation();
        this.valid = true;
        this.errorMsg = [];
        this.$store.updateCts();
        this.$store.updateVdis();
        this.$store.updateInputs();
      }, 300);
    },

    ctValidation(showErrors) {
      this.errorMsg = [];
      const items = [
        this.editedItem.cts[0].channel,
        this.editedItem.cts[1].channel,
        this.editedItem.cts[2].channel,
      ];
      if ((items[0] === items[1] && items[0] !== 0)
          || (items[1] === items[2] && items[1] !== 0)
          || (items[2] === items[0] && items[2] !== 0)) {
        if (showErrors) {
          this.errorMsg.push('Cannot use a CT multiple times.');
        }
        return false;
      }

      const uniqueItems = Array.from(new Set(items));
      const index = uniqueItems.indexOf(0);
      if (index >= -1) {
        uniqueItems.splice(index, 1);
      }


      if (this.storeState.data.inputs[this.editedItem.input].service === 4) { // Split phase
        if (uniqueItems.length === 0) {
          if (showErrors) {
            this.errorMsg.push('Please select at least one CT\'s.');
          }
          return false;
        }
        /*
        // Disabled, because we allow one CT to be selected for split phase.
        if (uniqueItems.length !== 2) {
          if (showErrors) {
            this.errorMsg.push('Please select both CT\'s.');
          }
          return false;
        }
        */
      } else if (uniqueItems.length === 0) {
        if (showErrors) {
          this.errorMsg.push('Please select at least one CT.');
        }
        return false;
      }

      if (uniqueItems.length === 3 && uniqueItems.includes(0)) {
        if (showErrors) {
          this.errorMsg.push(
            'Please select CT\'s for every phase or for only one phase',
          );
        }
        return false;
      }
      return true;
    },

    validate() {
      let valid = true;
      valid = this.$refs.form.validate() && valid;
      valid = this.ctValidation(true) && valid;
      this.valid = valid;
    },

    save() {
      this.validate();
      if (!this.valid) {
        return;
      }
      Object.keys(this.editedItem.cts).forEach((key) => {
        this.editedItem.cts[key].active = this.editedItem.cts[key].channel !== 0;
      });
      if (this.editedIndex > -1) {
        Object.assign(this.storeState.data.loads[this.editedIndex], this.editedItem);
      } else {
        this.editedItem.id = this.storeState.data.loads.length;
        this.storeState.data.loads.push(this.editedItem);
      }
      this.$store.updateCts();
      this.$store.updateVdis();
      this.close();
      this.storeState.loadDirty = true;
    },
    servicePhases(item) {
      let phases = 0;
      if (item >= 0) {
        // eslint-disable-next-line
        const service = this.storeState.data.inputs[item].service;
        if (service === 1 // 4-Wire wye
            || service === 2 // 3-Wire delta
            || service === 3) { // 4-Wire delta
          phases = 3;
        } else if (service === 4 // 3-Wire single phase (split-phase)
                   || service === 5) { // 2-Wire single phase
          phases = 1;
        } else { // Should not happen, just in case
          phases = 3;
        }
      }
      return phases;
    },

    inputs() {
      if (this.storeState.data.deviceInfo.model === 3) {
        return [{ text: `Input 1 (${this.storeState.data.inputs[0].voltage === null ? 0 : this.storeState.data.inputs[0].voltage} VAC)`, value: 0 }];
      }
      return [{ text: `Input 1 (${this.storeState.data.inputs[0].voltage === null ? 0 : this.storeState.data.inputs[0].voltage} VAC)`, value: 0 },
        { text: `Input 2 (${this.storeState.data.inputs[1].voltage === null ? 0 : this.storeState.data.inputs[1].voltage} VAC)`, value: 1 }];
    },

    clearInvisibleCts(item) {
      if (this.servicePhases(item) === 1) {
        this.editedItem.cts[1].channel = 0;
        this.editedItem.cts[2].channel = 0;
      }
      if (this.servicePhases(item) === 2) {
        this.editedItem.cts[2].channel = 0;
      }
    },

    ctState(ct) {
      let state = 0;
      // eslint-disable-next-line
      const unsaved = this.storeState.data.allCts[ct.channel - 6].unsaved;
      if (unsaved && ct.active) {
        state = 0;
      } else if (!unsaved && ct.active) {
        state = 1;
      } else if (unsaved && !ct.active) {
        state = 2;
      } else if (!unsaved && !ct.active) {
        state = 3;
      }
      return state;
    },
    addLoad() {
      this.editedItem.cts[0].channel = 0;
      this.editedItem.cts[1].channel = 0;
      this.editedItem.cts[2].channel = 0;
      this.editedItem.active = true;
      this.editedItem.input = -1;
      this.editedItem.vdi = this.defaultVdi;
      this.dialog = true;
      this.updateUnusedCts();
    },
  },
};
</script>

<style>
table.v-table tbody td:first-child,
table.v-table tbody td:not(:first-child),
table.v-table tbody th:first-child,
table.v-table tbody th:not(:first-child),
table.v-table thead td:first-child,
table.v-table thead td:not(:first-child),
table.v-table thead th:first-child,
table.v-table thead th:not(:first-child) {
  padding: 0 10px
}
</style>
